"use client";

import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { AlertCircle, RefreshCw, Home, Mail } from "lucide-react";
import Link from "next/link";

interface ErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
}

export default function Error({ error, reset }: ErrorProps) {
  useEffect(() => {
    // Log the error to an error reporting service
    console.error("Application Error:", error);
  }, [error]);

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-4 overflow-hidden relative">
      {/* Ambient Background Glows */}
      <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-red-900/10 blur-[120px] rounded-full pointer-events-none" />
      <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-accent-primary/5 blur-[120px] rounded-full pointer-events-none" />

      <div className="relative z-10 flex flex-col items-center text-center max-w-lg w-full">
        {/* Animated Error Graphic */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="mb-10 relative"
        >
          <div className="w-24 h-24 bg-red-900/20 rounded-3xl flex items-center justify-center border border-red-700/30 backdrop-blur-sm relative z-10">
            <AlertCircle size={48} className="text-red-500" />
          </div>
          {/* Decorative Circles */}
          <motion.div 
            animate={{ scale: [1, 1.2, 1], opacity: [0.2, 0.1, 0.2] }}
            transition={{ duration: 4, repeat: Infinity }}
            className="absolute inset-0 bg-red-500 rounded-full blur-2xl -z-0"
          />
        </motion.div>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="w-full"
        >
          <h1 className="text-3xl font-bold text-white mb-4 tracking-tight">
            System Interruption
          </h1>
          <p className="text-silver-gray text-lg mb-8 leading-relaxed">
            Something unexpected happened on our end. We&apos;ve logged the issue and our team is looking into it.
          </p>

          {/* Error reference if available */}
          {error.digest && (
            <div className="mb-8 px-4 py-2 bg-charcoal/50 border border-dark-slate rounded-lg inline-block">
              <span className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mr-2">Ref ID:</span>
              <code className="text-xs text-silver-gray font-mono">{error.digest}</code>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col gap-3 w-full max-w-sm mx-auto">
            <button
              onClick={() => reset()}
              className="flex items-center justify-center gap-2 px-8 py-3.5 bg-accent-primary text-white rounded-xl font-medium hover:bg-accent-blue transition-all active:scale-95 shadow-lg shadow-accent-primary/20 group"
            >
              <RefreshCw size={18} className="group-hover:rotate-180 transition-transform duration-500" />
              Try Reconnecting
            </button>
            
            <div className="grid grid-cols-2 gap-3">
              <Link
                href="/"
                className="flex items-center justify-center gap-2 px-4 py-3 bg-charcoal text-white rounded-xl font-medium border border-dark-slate hover:bg-onyx transition-all active:scale-95"
              >
                <Home size={16} />
                Home
              </Link>
              <a
                href="mailto:support@assetilo.com"
                className="flex items-center justify-center gap-2 px-4 py-3 bg-charcoal text-white rounded-xl font-medium border border-dark-slate hover:bg-onyx transition-all active:scale-95"
              >
                <Mail size={16} />
                Support
              </a>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Footer Decoration */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8, duration: 1 }}
        className="absolute bottom-10 left-1/2 -translate-x-1/2 flex items-center gap-3"
      >
        <div className="h-[1px] w-12 bg-linear-to-r from-transparent to-dark-slate" />
        <span className="text-[10px] uppercase tracking-[0.3em] text-neutral-600 font-medium">
          Assetilo Diagnostics
        </span>
        <div className="h-[1px] w-12 bg-linear-to-l from-transparent to-dark-slate" />
      </motion.div>
    </div>
  );
}
